/**
 * OverviewLanding — top of the Project Detail Overview tab.
 *
 * One block: <OverviewHero /> — title, description, state line, org line,
 * progress, schedule signal, and an Edit affordance. The previous
 * AttributesCard and chip strip are gone; everything that orients you
 * lives here, and the rest of the page is narrative (Status Pulse,
 * RAID, Team, Comments). The chip strip duplicated the tab bar; the
 * AttributesCard duplicated hero fields and pushed Status Pulse below
 * the fold. Full edit lives in EditProjectDialog (pencil, top-right).
 */
import { useEffect, useRef, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { Loader2, Pencil, Save, X } from "lucide-react";
import { toast } from "sonner";
import { EditProjectDialog } from "@/components/portfolio/EditProjectDialog";
import { cn } from "@/lib/cn";
import { DependsOnChips } from "@/components/project/DependsOn";
import { humanDateRange, formatDate } from "@/lib/format";
import {
  HEALTH_OPTIONS,
  STATUS_BY_KEY,
} from "@/lib/projectStatus";
import { useUpdateProject, useRebaselineProject, useProjectBaselines } from "@/hooks/usePortfolio";
import { useProjectJiraIssues } from "@/hooks/useJira";
import { useSearchParams } from "react-router-dom";
import { useAppSettings } from "@/hooks/useAppSettings";
import { costEstimateFor, costLabelFor, costRangeLabel, readBlendedRate } from "@/lib/cost";
import { tierChipText, TIER_META } from "@/lib/priorityTier";
import type { Project } from "@/types/project";

const PHASE_LABEL: Record<string, string> = {
  planning: "Planning",
  execution: "Execution",
  testing_go_live: "Testing / Go Live",
};

// ---------------------------------------------------------------------------
// Hero strip
// ---------------------------------------------------------------------------

interface HeroProps {
  project: Project;
}

export function OverviewHero({
  project: p,
  pinnedLinks,
  latestPulse,
}: HeroProps & { pinnedLinks?: React.ReactNode; latestPulse?: React.ReactNode }) {
  // Apple-style page header: the project is the document, this is its
  // cover page. Everything earns its place — title is the anchor, meta
  // splits into two thin lines (state, then org/who), and color appears
  // only when it carries signal (health dot + off-plan slip). No card
  // chrome — this is a header, not a tile.
  const [editOpen, setEditOpen] = useState(false);
  // Cost rate — same source as Portfolio table + Edit dialog. Drives
  // the inline Est. Cost chip below.
  const { data: costSettings } = useAppSettings("cost");
  const blendedRate = readBlendedRate(costSettings);
  const statusMeta = p.status ? STATUS_BY_KEY[p.status as keyof typeof STATUS_BY_KEY] : null;
  const healthMeta = p.health ? HEALTH_OPTIONS.find((h) => h.key === p.health) : null;
  const pct = Math.round((p.pct_complete ?? 0) * 100);
  const dateRange = humanDateRange(p.start_date, p.end_date);

  // Two meta lines so the row doesn't wrap awkwardly with 8 entries:
  //   state  — health, status, priority, dates    (the live signal)
  //   org    — PM, sponsor, team, phase           (who and where)
  // Empty fields are hidden, not "—" — silence reads as "nothing to
  // act on." Both lines render in slate-500 with dot separators.
  const stateMeta: Array<{ key: string; node: React.ReactNode }> = [];
  if (healthMeta) {
    stateMeta.push({
      key: "health",
      node: (
        <span className="inline-flex items-center gap-1.5">
          <span className={cn("inline-block h-2 w-2 rounded-full", healthMeta.dotClass)} />
          <span>{healthMeta.label}</span>
        </span>
      ),
    });
  }
  if (statusMeta) stateMeta.push({ key: "status", node: <span>{statusMeta.label}</span> });
  if (p.priority) stateMeta.push({ key: "priority", node: <span>{p.priority}</span> });
  if (dateRange) stateMeta.push({ key: "dates", node: <span className="tabular-nums">{dateRange}</span> });

  const orgMeta: Array<{ key: string; node: React.ReactNode }> = [];
  // PM is ALWAYS rendered (even empty) — like Sponsor, this is the
  // inline add/edit path; the Top Projects board reads this field, so
  // hiding it when unset would leave no way to fill it in. (Brett,
  // 2026-09-03.)
  orgMeta.push({
    key: "pm",
    node: <PmMeta projectId={p.id} value={p.pm ?? null} />,
  });
  // Sponsor is ALWAYS rendered — even when empty — because it's the
  // only place a user can add/change it inline. Hidden = no edit path.
  // effectiveSponsor is what the API resolves for display; the raw
  // `sponsor` field is what edits write to.
  orgMeta.push({
    key: "sponsor",
    node: (
      <SponsorMeta
        projectId={p.id}
        ownValue={p.sponsor ?? null}
        effectiveValue={p.effective_sponsor ?? null}
      />
    ),
  });
  if (p.team) orgMeta.push({ key: "team", node: <LabeledMeta label="Team" value={p.team} /> });
  // Phase intentionally NOT shown here — it duplicates the Status pill
  // in stateMeta (status='testing_go_live' renders as the same label
  // 'Testing / Go Live'). Keeping it would be visual noise. If we
  // ever decouple phase from status, surface this again.
  // T-shirt + computed cost range. Only render when t-shirt is set —
  // hero design rule is "silence reads as nothing to act on," and "—"
  // here adds noise without information. Once a t-shirt is picked, both
  // the size label and the cost range read together: "Size M · $5.2k–$10.4k".
  if (p.tshirt_size) {
    const sizeLabel = p.tshirt_size.split(":")[0]; // "M: 80-160 Hours" → "M"
    orgMeta.push({
      key: "tshirt",
      node: <LabeledMeta label="Size" value={sizeLabel} />,
    });
    // Est. Cost follows the precedence rule in lib/cost: a phase-hour
    // breakdown (or est_hours) gives a point estimate; the size range
    // stays as muted context so the two never look like a contradiction.
    const est = costEstimateFor(p);
    orgMeta.push({
      key: "est_cost",
      node:
        est?.kind === "point" ? (
          <LabeledMeta
            label="Est. Cost"
            value={
              <span title={`${est.hours} h × $${blendedRate}/h — from est. hours`}>
                {costLabelFor(p, blendedRate)}
                <span className="ml-1 font-normal text-ink-subtle">
                  ({est.hours} h) · sized {sizeLabel} {costRangeLabel(p.tshirt_size, blendedRate)}
                </span>
              </span>
            }
          />
        ) : (
          <LabeledMeta label="Est. Cost" value={costRangeLabel(p.tshirt_size, blendedRate)} />
        ),
    });
  }

  // Schedule signal — slip is now part of the progress block, but
  // staleness (no edits in N days) is its own thing and gets a
  // line below.
  const slipDays = p.slip_days;
  const isComplete = pct >= 100;
  const slipBehind = slipDays != null && slipDays > 0;
  // Bar colour follows the PM's HEALTH call (Brett, 2026-10-05): green /
  // yellow / red, emerald when complete, neutral when not assessed. Slip
  // is said in words next to the percentage instead of colouring the bar
  // — a 31-day slip was painting a green project red with no label.
  const progressTone = isComplete ? "complete" : (p.health ?? "none");
  const progressFillClass = {
    complete: "bg-emerald-500",
    green: "bg-emerald-500",
    yellow: "bg-amber-500",
    red: "bg-red-500",
    none: "bg-brand-navy-500",
  }[progressTone] ?? "bg-brand-navy-500";
  void slipBehind;
  return (
    <header className="relative rounded-xl border border-line bg-surface p-6 shadow-sm">
      {/* Edit affordance — top-right corner, ghost button. Hover reveals
          the indigo accent. Opens the existing dialog so every field
          (description, sponsor, team, BA/PM, t-shirt, phase, initiative,
          spec dates) stays editable without a second surface on the page. */}
      <button
        onClick={() => setEditOpen(true)}
        className="absolute right-0 top-0 inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs text-ink-subtle hover:bg-raised hover:text-brand-navy-600 dark:hover:text-brand-navy-400 transition-colors"
        title="Edit project details"
      >
        <Pencil className="h-3.5 w-3.5" />
        Edit
      </button>

      {/* Project ID — caption above the title, like a document number.
          Priority chip beside it when the project is on the Top
          Projects board ("Top 10 · #3" / "Just Do Its" / ...). */}
      <div className="flex items-center gap-2">
        <span className="font-mono text-[11px] uppercase tracking-[0.12em] text-ink-subtle">
          {p.id}
        </span>
        {(() => {
          const chip = tierChipText(p);
          const meta = p.priority_tier ? TIER_META[p.priority_tier] : undefined;
          if (!chip || !meta) return null;
          return (
            <Link
              to="/top-projects"
              className={cn(
                "rounded-full px-2 py-0.5 text-[10px] font-semibold",
                meta.chipClass,
              )}
              title="On the Top Projects board — click to open it"
            >
              {chip}
            </Link>
          );
        })()}
        {/* Dependency chips — "after ETE-319", editable here and on the
            board (same component, same data). Visual only. */}
        <DependsOnChips projectId={p.id} canEdit />
      </div>

      {/* Title — page anchor. Tracking-tight + display weight echoes the
          system font treatment Apple uses for page headers. */}
      <h1 className="mt-1.5 text-3xl font-semibold tracking-tight text-ink">
        {p.name}
      </h1>

      {/* Description — the subtitle. Plain (not italic), text-base,
          slate-500. Anyone landing cold orients here in 2 seconds. */}
      <DescriptionLine projectId={p.id} value={p.description ?? null} />

      {/* Problem / Goal statements (Brett, 2026-09-04) — the framing
          pair every project should answer. Always rendered with their
          headings so the empty state itself prompts for the content. */}
      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        <StatementBlock
          projectId={p.id}
          field="problem_statement"
          label="Problem Statement"
          placeholder="What hurts today? Who feels it, and what does it cost?"
          value={p.problem_statement ?? null}
        />
        <StatementBlock
          projectId={p.id}
          field="goal_statement"
          label="Goal Statement"
          placeholder="What does done look like? How will we know it worked?"
          value={p.goal_statement ?? null}
        />
      </div>

      {/* State line — live signal: health, status, priority, dates */}
      {stateMeta.length > 0 && (
        <MetaLine className="mt-4" items={stateMeta} />
      )}

      {/* Org line — who and where: PM, sponsor, team, phase. Set off
          with slightly less vertical space so the two lines read as one
          stacked block, not two separate things. */}
      {orgMeta.length > 0 && (
        <MetaLine className="mt-1.5" items={orgMeta} />
      )}

      {/* Progress + slip read as one statement: '50% complete · 14 days
          behind baseline'. The bar color itself encodes urgency (amber
          for slipping, red for >30 days, emerald when complete) so a
          glance at the bar tells you both 'how far' and 'how worried.'
          Re-baseline button appears when slip > 0 — lets the user say
          'this isn't slip, scope changed' and reset the baseline. */}
      <div className="mt-5">
        <div className="flex items-baseline justify-between gap-3">
          <div className="text-sm tabular-nums">
            <span className="font-semibold text-ink">{pct}%</span>
            <span className="text-ink-muted"> complete</span>
            {p.jira_pct_complete != null && (
              <span
                className="ml-2 text-[11px] font-normal text-ink-subtle"
                title="Rolled up from linked Jira issues — a reference, not the source of truth. Edit % complete in the planner."
              >
                · Jira: {Math.round(p.jira_pct_complete * 100)}%
              </span>
            )}
            {(slipDays != null && slipDays !== 0) || p.is_stale ? (
              <span className="ml-2 text-[11px]" data-testid="slip-label">
                · <SlipDisplay days={slipDays ?? null} stale={Boolean(p.is_stale)} baseline={p.original_end_date ?? null} />
              </span>
            ) : null}
          </div>
          {/* Always render — scope can change pre-emptively (before
              slip shows up) or repeatedly (Phase 3 after Phase 2 was
              already absorbed). Gating on slip > 0 hid the action right
              when the user might need it again. */}
          {p.end_date && (
            <RebaselineButton projectId={p.id} currentEnd={p.end_date} prevBaseline={p.original_end_date} />
          )}
        </div>
        <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-muted">
          <div
            className={cn("h-full rounded-full transition-all", progressFillClass)}
            style={{ width: `${pct}%` }}
            data-testid="progress-fill"
            data-tone={progressTone}
          />
        </div>
        {/* Last re-baseline event surfaced for context — answers
            'why did the baseline move?' for sponsors who notice. */}
        <BaselineHistoryHint projectId={p.id} />
        {/* Linked Jira status — done count + overdue. Replaces the
            standalone JiraScheduleStrip card. Lives here because Jira
            completion is just another status signal alongside project
            progress — same row family, not a separate concern. Renders
            nothing when the project has no Jira links. */}
        <JiraInlineStatus projectId={p.id} />
      </div>

      <EditProjectDialog
        project={editOpen ? p : null}
        open={editOpen}
        onOpenChange={setEditOpen}
      />

      {/* Footer band — pinned chips + "Latest" status pulse one-liner.
          Same treatment as InitiativeHero so the chips visibly belong
          to the hero card and the latest pulse beat sits beside them. */}
      {(pinnedLinks || latestPulse) && (
        <div className="-mx-6 -mb-6 mt-5 rounded-b-xl border-t border-line-soft bg-canvas/60 px-6 py-3">
          <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
            <div className="min-w-0 flex-shrink">{pinnedLinks}</div>
            {latestPulse && (
              <div className="min-w-0 max-w-full flex-1 sm:flex-none sm:max-w-[55%] sm:text-right">
                {latestPulse}
              </div>
            )}
          </div>
        </div>
      )}
    </header>
  );
}

function MetaLine({
  items,
  className,
}: {
  items: Array<{ key: string; node: React.ReactNode }>;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "flex flex-wrap items-center gap-x-2.5 gap-y-1 text-sm text-ink-muted",
        className,
      )}
    >
      {items.map((m, i) => (
        <span key={m.key} className="inline-flex items-center gap-2.5">
          {i > 0 && <span className="text-ink-faint" aria-hidden>·</span>}
          {m.node}
        </span>
      ))}
    </div>
  );
}

function LabeledMeta({ label, value }: { label: string; value: ReactNode }) {
  return (
    <span>
      <span className="text-ink-subtle">{label}</span>{" "}
      <span className="text-ink-2">{value}</span>
    </span>
  );
}

/**
 * Inline-editable Sponsor field for the org meta row.
 *
 * Plain text input — sponsors are business stakeholders (VPs, directors,
 * project requestors), not IT roster members. Type whatever name applies.
 * Enter saves, Esc cancels, blur commits.
 *
 * Props split between `ownValue` (the project's raw sponsor — what edit
 * writes to) and `effectiveValue` (what to display, as resolved by the API).
 */
// PM — same inline click-to-edit pattern as SponsorMeta, without the
// initiative inheritance (PMs don't inherit; a project can have its own
// operating PM even when its parent initiative is run by someone else).
function PmMeta({ projectId, value }: { projectId: string; value: string | null }) {
  const updateProject = useUpdateProject();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value ?? "");
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!editing) setDraft(value ?? "");
  }, [value, editing]);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  const save = async (next: string) => {
    const trimmed = next.trim();
    if (trimmed === (value ?? "").trim()) {
      setEditing(false);
      return;
    }
    try {
      await updateProject.mutateAsync({ id: projectId, pm: trimmed || null } as any);
      setEditing(false);
    } catch (err: any) {
      toast.error(`Failed to save PM: ${err?.message ?? "unknown"}`);
    }
  };

  if (editing) {
    return (
      <span className="inline-flex items-center gap-2">
        <span className="text-ink-subtle">PM</span>
        <input
          ref={inputRef}
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={() => void save(draft)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              void save(draft);
            } else if (e.key === "Escape") {
              e.preventDefault();
              setDraft(value ?? "");
              setEditing(false);
            }
          }}
          placeholder="Project manager"
          className="rounded-md border border-brand-navy-300 dark:border-brand-navy-500/40 bg-surface px-2 py-0.5 text-sm text-ink-2 outline-none ring-2 ring-brand-navy-100 dark:ring-brand-navy-500/30 focus:border-brand-navy-400"
          style={{ minWidth: "200px" }}
        />
      </span>
    );
  }

  return (
    <span
      onClick={() => setEditing(true)}
      title="Click to edit PM"
      className="group inline-flex cursor-pointer items-center gap-1"
    >
      <span className="text-ink-subtle">PM</span>{" "}
      {value ? (
        <span className="text-ink-2 group-hover:text-brand-navy-700 group-hover:underline">{value}</span>
      ) : (
        <span className="italic text-ink-subtle group-hover:text-brand-navy-600">+ Add</span>
      )}
    </span>
  );
}

function SponsorMeta({
  projectId,
  ownValue,
  effectiveValue,
}: {
  projectId: string;
  ownValue: string | null;
  effectiveValue: string | null;
}) {
  const updateProject = useUpdateProject();
  const [editing, setEditing] = useState(false);
  // Edit always operates on the project's own value.
  const [draft, setDraft] = useState(ownValue ?? "");
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!editing) setDraft(ownValue ?? "");
  }, [ownValue, editing]);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [editing]);

  const save = async (next: string) => {
    const trimmed = next.trim();
    const current = (ownValue ?? "").trim();
    if (trimmed === current) {
      setEditing(false);
      return;
    }
    try {
      await updateProject.mutateAsync({
        id: projectId,
        sponsor: trimmed || null,
      } as any);
      setEditing(false);
    } catch (err: any) {
      toast.error(`Failed to save sponsor: ${err?.message ?? "unknown"}`);
    }
  };

  if (editing) {
    return (
      <span className="inline-flex items-center gap-2">
        <span className="text-ink-subtle">Sponsor</span>
        <input
          ref={inputRef}
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onBlur={() => void save(draft)}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              void save(draft);
            } else if (e.key === "Escape") {
              e.preventDefault();
              setDraft(ownValue ?? "");
              setEditing(false);
            }
          }}
          placeholder="Sponsor name"
          className="rounded-md border border-brand-navy-300 dark:border-brand-navy-500/40 bg-surface px-2 py-0.5 text-sm text-ink-2 outline-none ring-2 ring-brand-navy-100 dark:ring-brand-navy-500/30 focus:border-brand-navy-400"
          style={{ minWidth: "240px" }}
        />
      </span>
    );
  }

  const display = effectiveValue && effectiveValue.trim().length > 0 ? effectiveValue : null;

  return (
    <span
      onClick={() => setEditing(true)}
      title="Click to edit sponsor"
      className="group inline-flex cursor-pointer items-center gap-1"
    >
      <span className="text-ink-subtle">Sponsor</span>{" "}
      {display ? (
        <span className="text-ink-2 group-hover:text-brand-navy-700 group-hover:underline">{display}</span>
      ) : (
        <span className="italic text-ink-subtle group-hover:text-brand-navy-600">+ Add</span>
      )}
      <Pencil className="h-3 w-3 opacity-0 transition-opacity group-hover:opacity-50" />
    </span>
  );
}

/**
 * Inline-editable elevator-pitch line. Empty state is a faint click
 * target ("Add a one-line description…") so the affordance is present
 * but not loud. Save commits on blur or Enter; Esc cancels. We keep the
 * data model as plain text (line breaks allowed) — markdown-style
 * formatting is intentionally out of scope.
 */
// Problem / Goal statement block — a labeled, click-to-edit paragraph.
// The heading is ALWAYS visible (the "pre-fill"): an empty block reads
// as a prompt to write one, not as missing chrome.
function StatementBlock({
  projectId,
  field,
  label,
  placeholder,
  value,
}: {
  projectId: string;
  field: "problem_statement" | "goal_statement";
  label: string;
  placeholder: string;
  value: string | null;
}) {
  const updateProject = useUpdateProject();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value ?? "");

  useEffect(() => {
    if (!editing) setDraft(value ?? "");
  }, [value, editing]);

  const save = async () => {
    const next = draft.trim();
    if (next === (value ?? "").trim()) {
      setEditing(false);
      return;
    }
    try {
      await updateProject.mutateAsync({ id: projectId, [field]: next } as any);
      setEditing(false);
    } catch {
      toast.error(`Failed to save ${label.toLowerCase()}.`);
    }
  };

  return (
    <div className="rounded-lg border border-line-soft bg-canvas/50 p-3">
      <div className="mb-1 text-[10px] font-bold uppercase tracking-wider text-ink">
        {label}
      </div>
      {editing ? (
        <div>
          <textarea
            autoFocus
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Escape") {
                setDraft(value ?? "");
                setEditing(false);
              } else if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) void save();
            }}
            rows={3}
            maxLength={1000}
            placeholder={placeholder}
            className="w-full resize-y rounded-md border border-line-strong bg-surface px-2.5 py-1.5 text-sm leading-relaxed text-ink-2 placeholder:text-ink-subtle focus:border-brand-navy-400 focus:outline-none focus:ring-1 focus:ring-brand-navy-400"
          />
          <div className="mt-1 flex justify-end gap-1.5">
            <button
              onClick={() => {
                setDraft(value ?? "");
                setEditing(false);
              }}
              className="rounded-md border border-line bg-surface px-2 py-1 text-[10px] font-medium text-ink-muted hover:bg-raised"
            >
              Cancel
            </button>
            <button
              onClick={() => void save()}
              disabled={updateProject.isPending}
              className="flex items-center gap-1 rounded-md bg-brand-navy-600 px-2 py-1 text-[10px] font-medium text-white hover:bg-brand-navy-700 disabled:opacity-50"
              title="Save (Cmd+Enter)"
            >
              {updateProject.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
              Save
            </button>
          </div>
        </div>
      ) : value && value.trim() ? (
        <button
          onClick={() => setEditing(true)}
          className="group block w-full text-left"
          title="Click to edit"
        >
          <p className="whitespace-pre-wrap text-sm leading-relaxed text-ink-3 transition-colors group-hover:text-ink">
            {value}
          </p>
        </button>
      ) : (
        <button
          onClick={() => setEditing(true)}
          className="block text-left text-sm italic text-ink-faint transition-colors hover:text-brand-navy-500"
        >
          {placeholder}
        </button>
      )}
    </div>
  );
}

function DescriptionLine({ projectId, value }: { projectId: string; value: string | null }) {
  const updateProject = useUpdateProject();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value ?? "");

  // Re-sync draft when underlying value changes (another tab saved).
  useEffect(() => {
    if (!editing) setDraft(value ?? "");
  }, [value, editing]);

  const handleSave = async () => {
    const next = draft.trim();
    if (next === (value ?? "").trim()) {
      setEditing(false);
      return;
    }
    try {
      await updateProject.mutateAsync({ id: projectId, description: next } as any);
      setEditing(false);
    } catch {
      toast.error("Failed to save description.");
    }
  };

  const handleCancel = () => {
    setDraft(value ?? "");
    setEditing(false);
  };

  if (editing) {
    const remaining = 500 - draft.length;
    return (
      <div className="mt-2 flex items-start gap-2">
        <div className="flex-1">
          <textarea
            autoFocus
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Escape") handleCancel();
              else if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) handleSave();
            }}
            rows={2}
            maxLength={500}
            placeholder="One sentence — what is this project, in plain language?"
            className="w-full resize-y rounded-md border border-line-strong bg-surface px-3 py-2 text-base leading-relaxed text-ink-2 placeholder:text-ink-subtle focus:border-brand-navy-400 focus:outline-none focus:ring-1 focus:ring-brand-navy-400"
          />
          {/* Small inline character counter — slate by default, amber when
              ≤50 left, red when at the cap. Lives in the field row so it
              doesn't add vertical noise to the page. */}
          <div className={cn(
            "mt-0.5 text-right text-[10px] tabular-nums transition-colors",
            remaining <= 0 ? "text-rose-500"
              : remaining <= 50 ? "text-amber-600 dark:text-amber-400"
              : "text-ink-subtle",
          )}>
            {draft.length} / 500
          </div>
        </div>
        <div className="flex flex-col gap-1">
          <button
            onClick={handleSave}
            disabled={updateProject.isPending}
            className="flex items-center gap-1 rounded-md bg-brand-navy-600 px-2 py-1 text-[10px] font-medium text-white hover:bg-brand-navy-700 transition-colors disabled:opacity-50"
            title="Save (Cmd+Enter)"
          >
            {updateProject.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
          </button>
          <button
            onClick={handleCancel}
            className="flex items-center gap-1 rounded-md border border-line bg-surface px-2 py-1 text-[10px] font-medium text-ink-muted hover:bg-raised transition-colors"
            title="Cancel (Esc)"
          >
            <X className="h-3 w-3" />
          </button>
        </div>
      </div>
    );
  }

  if (value && value.trim()) {
    return (
      <button
        onClick={() => setEditing(true)}
        className="group mt-2 block w-full rounded text-left"
        title="Click to edit"
      >
        <p className="whitespace-pre-wrap text-base leading-relaxed text-ink-muted group-hover:text-ink-2 transition-colors">
          {value}
        </p>
      </button>
    );
  }

  return (
    <button
      onClick={() => setEditing(true)}
      className="mt-2 block text-left text-base text-ink-faint hover:text-brand-navy-500 transition-colors"
    >
      Add a one-line description…
    </button>
  );
}

function SlipDisplay({
  days,
  stale,
  baseline,
}: {
  days: number | null;
  stale: boolean;
  baseline: string | null;
}) {
  // Caller is responsible for only rendering when there's actual signal
  // — see OverviewHero. We still null-guard for safety.
  if (days == null && !stale) return null;
  let label: React.ReactNode = null;
  if (days != null && days > 0) {
    const cls = days > 14 ? "text-rose-600 dark:text-rose-400" : "text-amber-700 dark:text-amber-300";
    label = (
      <span className={cn("font-medium", cls)}>
        {baseline && <span className="text-ink-muted font-normal">Baseline {formatDate(baseline)} · </span>}
        {days} days behind
      </span>
    );
  } else if (days != null && days < 0) {
    label = (
      <span className="font-medium text-emerald-600 dark:text-emerald-400">
        {baseline && <span className="text-ink-muted font-normal">Baseline {formatDate(baseline)} · </span>}
        {Math.abs(days)} days early
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-2">
      {label}
      {stale && (
        <span className="inline-flex items-center gap-1.5 text-amber-700 dark:text-amber-300">
          <span className="inline-block h-1.5 w-1.5 rounded-full bg-amber-400" />
          <span>plan may be out of sync</span>
        </span>
      )}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Re-baseline button + modal — surfaces when the project is showing as
// slipping. Lets the user say 'this isn't slip, scope changed' and
// reset the baseline to current end_date. Optional reason for the
// audit trail (kept in project_baselines table).
// ---------------------------------------------------------------------------

function RebaselineButton({
  projectId,
  currentEnd,
  prevBaseline,
}: {
  projectId: string;
  currentEnd: string | null;
  prevBaseline: string | null;
}) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState("");
  const rebaseline = useRebaselineProject();

  const submit = () => {
    rebaseline.mutate(
      { id: projectId, reason: reason.trim() || undefined },
      {
        onSuccess: () => {
          toast.success("Baseline updated");
          setOpen(false);
          setReason("");
        },
        onError: () => {
          toast.error("Couldn't update baseline");
        },
      },
    );
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1 rounded-md border border-line bg-surface px-2 py-0.5 text-[11px] font-medium text-ink-3 hover:border-line-strong hover:bg-raised hover:text-ink"
        title="Move the baseline to the current end date (use for scope changes, not for slip)"
      >
        Re-baseline
      </button>
      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4">
          <div className="w-full max-w-md rounded-xl bg-surface p-5 shadow-xl">
            <h3 className="text-sm font-semibold text-ink">Re-baseline this project?</h3>
            <p className="mt-2 text-xs text-ink-3">
              This moves the baseline from{" "}
              <strong className="text-ink">{prevBaseline ? formatDate(prevBaseline) : "—"}</strong>{" "}
              to{" "}
              <strong className="text-ink">{currentEnd ? formatDate(currentEnd) : "—"}</strong>.
              Slip resets to 0. Use this when the work itself changed
              (scope added, phase merged in, dependency moved) — not
              when you're just running late.
            </p>
            <div className="mt-4">
              <label className="block text-[11px] font-semibold uppercase tracking-wider text-ink-muted">
                Reason (optional, for audit)
              </label>
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                rows={3}
                placeholder="e.g. Phase 2 work absorbed into this project"
                className="mt-1 w-full resize-none rounded-md border border-line bg-surface px-2.5 py-2 text-xs text-ink-2 placeholder:text-ink-subtle focus:border-brand-navy-300 focus:outline-none focus:ring-1 focus:ring-brand-navy-300"
              />
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button
                onClick={() => {
                  setOpen(false);
                  setReason("");
                }}
                className="rounded-md border border-line bg-surface px-3 py-1.5 text-xs font-medium text-ink-3 hover:bg-raised"
              >
                Cancel
              </button>
              <button
                onClick={submit}
                disabled={rebaseline.isPending}
                className="rounded-md bg-brand-navy-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-brand-navy-700 disabled:opacity-50"
              >
                {rebaseline.isPending ? "Re-baselining…" : "Re-baseline"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Baseline history hint — small inline note showing the most recent
// re-baseline event when there is one. Answers 'why did the baseline
// move?' without making the user click into a separate page.
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Inline Jira status — one-line summary of linked Jira completion +
// overdue count, with a click-through to the Jira tab. Lives in the
// hero's progress block. Renders nothing when the project has no
// linked Jira issues. Replaces the old standalone JiraScheduleStrip
// card — same data, less page real estate, colocated with other
// status signals.
// ---------------------------------------------------------------------------

function JiraInlineStatus({ projectId }: { projectId: string }) {
  const q = useProjectJiraIssues(projectId);
  const [, setSearchParams] = useSearchParams();

  const stats = (() => {
    const all: Array<{ status_category?: string | null; due_date?: string | null; dev_end_date?: string | null }> = [];
    for (const i of q.data?.issues ?? []) {
      all.push(i);
      if (i.children) all.push(...i.children);
    }
    if (all.length === 0) return null;
    const today = (() => {
      const d = new Date();
      d.setHours(0, 0, 0, 0);
      return d.getTime();
    })();
    let done = 0;
    let overdue = 0;
    for (const i of all) {
      if (i.status_category === "done") done += 1;
      // Dev End Date is what the team schedules by (the Jira tab shows it);
      // the built-in Due Date is only a fallback (2026-10-05, SSE-803).
      const due = (i.dev_end_date ?? i.due_date)?.slice(0, 10);
      if (due && i.status_category !== "done") {
        const t = new Date(due + "T00:00:00").getTime();
        if (!Number.isNaN(t) && t < today) overdue += 1;
      }
    }
    return { total: all.length, done, overdue };
  })();

  if (!stats) return null;

  const openJiraTab = () => {
    const next = new URLSearchParams();
    next.set("tab", "jira");
    setSearchParams(next, { replace: false });
  };

  return (
    <button
      onClick={openJiraTab}
      className="mt-2 inline-flex items-baseline gap-1.5 text-xs hover:underline underline-offset-2 decoration-ink-faint hover:decoration-ink-muted"
      title="Open the Jira tab"
    >
      <span className="font-medium text-ink-3">Linked Jira:</span>
      <span className="tabular-nums text-ink-muted">
        {stats.done} of {stats.total} done
      </span>
      {stats.overdue > 0 && (
        <>
          <span className="text-ink-faint">·</span>
          <span className="font-semibold text-red-600 dark:text-red-400 tabular-nums">
            {stats.overdue} overdue
          </span>
        </>
      )}
    </button>
  );
}

function BaselineHistoryHint({ projectId }: { projectId: string }) {
  const { data } = useProjectBaselines(projectId);
  const latest = data?.[0];
  if (!latest) return null;
  return (
    <div className="mt-2 text-xs text-ink-muted">
      <span className="font-medium text-ink-3">Re-baselined</span>{" "}
      {formatDate(latest.set_at.slice(0, 10))}
      {latest.prev_end_date && (
        <>
          {" "}from{" "}
          <span className="tabular-nums">{formatDate(latest.prev_end_date)}</span>
        </>
      )}
      {latest.reason && (
        <>
          {" — "}
          <span className="italic text-ink-muted">{latest.reason}</span>
        </>
      )}
    </div>
  );
}

